/**
 * SoleadLab 2.0 - Monitoring Types
 *
 * Single source of truth for all monitoring TypeScript types.
 * Re-exports Zod schemas from monitoring_config_schema and adds
 * additional types for compound alarms, provider status, and runtime state.
 */

import { z } from 'zod';

// ============================================
// Zod Schemas (canonical, validated at save-time)
// ============================================

export const AlarmSeveritySchema = z.enum(['warning', 'critical']);
export type AlarmSeverity = z.infer<typeof AlarmSeveritySchema>;

export const AlarmOperatorSchema = z.enum(['>', '>=', '<', '<=', '==', '!=']);
export type AlarmOperator = z.infer<typeof AlarmOperatorSchema>;

export const DatapointGroupSchema = z.enum([
  'inverters',
  'meters',
  'sensors',
  'statuses',
  'ppcs',
  'calculations',
  'stringboxes',
  'batteries',
  'tracker',
  'basics',
  'other',
]);
export type DatapointGroup = z.infer<typeof DatapointGroupSchema>;

export const MetricKeySchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_\-:.]+$/);
export type MetricKey = z.infer<typeof MetricKeySchema>;

export const UnitSchema = z.string().min(1).max(32);
export type Unit = z.infer<typeof UnitSchema>;

// ============================================
// Alarm Rule (single condition)
// ============================================

export const AlarmRuleSchema = z.object({
  id: z.string().uuid(),
  enabled: z.boolean().default(true),
  metric: MetricKeySchema,
  op: AlarmOperatorSchema,
  value: z.number().finite(),
  severity: AlarmSeveritySchema,
  message: z.string().max(200).optional(),
});
export type AlarmRule = z.infer<typeof AlarmRuleSchema>;

// ============================================
// Compound Alarm Rule Group (AND/OR logic)
// ============================================

export const AlarmConditionSchema = z.object({
  metric: MetricKeySchema,
  op: AlarmOperatorSchema,
  value: z.number().finite(),
});
export type AlarmCondition = z.infer<typeof AlarmConditionSchema>;

export const AlarmRuleGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(120).optional(),
  combinator: z.enum(['AND', 'OR']),
  conditions: z.array(AlarmConditionSchema).min(1),
  severity: AlarmSeveritySchema,
  enabled: z.boolean().default(true),
  message: z.string().max(200).optional(),
});
export type AlarmRuleGroup = z.infer<typeof AlarmRuleGroupSchema>;

// ============================================
// Metric Option (checkbox grid)
// ============================================

export const MetricOptionSchema = z.object({
  key: MetricKeySchema,
  label: z.string().min(1).max(80).optional(),
  unit: UnitSchema.optional(),
  description: z.string().max(200).optional(),
});
export type MetricOption = z.infer<typeof MetricOptionSchema>;

// ============================================
// Data Point Config
// ============================================

export const DataPointConfigSchema = z.object({
  id: z.string().uuid(),
  group: DatapointGroupSchema.default('other'),
  display_name: z.string().min(1).max(120),
  entity_id: z.string().min(1).max(120),

  provider: z
    .object({
      name: z.string().min(1).max(80),
      source_id: z.string().min(1).max(200).optional(),
      pointer: z
        .object({
          json_path: z.string().min(1).max(500).optional(),
          category: z.string().min(1).max(80).optional(),
          entity: z.string().min(1).max(120).optional(),
        })
        .optional(),
    })
    .optional(),

  enabled: z.boolean().default(true),
  unit: UnitSchema.optional(),
  metric_units: z.record(z.string(), z.string()).optional(),
  decimals: z.number().int().min(0).max(6).default(2),
  poll_interval_seconds: z.number().int().min(5).max(86400).default(60),

  selected_metrics: z.array(MetricKeySchema).default([]),
  available_metrics: z.array(MetricOptionSchema).optional(),

  // Simple alarm rules (flat list)
  rules: z.array(AlarmRuleSchema).default([]),

  // Compound alarm rule groups (AND/OR)
  rule_groups: z.array(AlarmRuleGroupSchema).default([]),

  // Map placement
  coordinates: z.tuple([z.number(), z.number()]).optional(),

  ui: z
    .object({
      pinned: z.boolean().optional(),
      order: z.number().int().optional(),
      display_metric: z.string().optional(),
    })
    .optional(),

  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});
export type DataPointConfig = z.infer<typeof DataPointConfigSchema>;

// ============================================
// Runtime State (derived from live data, NEVER persisted)
// ============================================

export const AlarmStateSchema = z.enum(['ok', 'warning', 'critical', 'stale', 'no_data']);
export type AlarmState = z.infer<typeof AlarmStateSchema>;

export interface TriggeredRule {
  rule_id: string;
  metric: MetricKey;
  op: AlarmOperator;
  value: number;
  actual: number | string | null;
  severity: AlarmSeverity;
  message?: string;
}

export interface TriggeredRuleGroup {
  group_id: string;
  group_name?: string;
  combinator: 'AND' | 'OR';
  severity: AlarmSeverity;
  message?: string;
  triggered_conditions: Array<{
    metric: MetricKey;
    op: AlarmOperator;
    value: number;
    actual: number | string | null;
  }>;
}

export interface DataPointRuntimeState {
  datapoint_id: string;
  values: Record<string, number | string | null>;
  ts: string | null;
  state: AlarmState;
  triggered_rules: TriggeredRule[];
  triggered_groups: TriggeredRuleGroup[];
}

// ============================================
// Advanced Alarm Rules (site-level, cross-entity)
// ============================================

export const AdvancedRuleTypeSchema = z.enum(['zero_check', 'deviation', 'expression']);
export type AdvancedRuleType = z.infer<typeof AdvancedRuleTypeSchema>;

// Preset 1: Zero/Threshold Check by Pattern
export const ZeroCheckConfigSchema = z.object({
  metric_pattern: z.string().min(1),
  entity_type: z.string().optional(),
  op: AlarmOperatorSchema,
  value: z.number().finite(),
  match_mode: z.enum(['any', 'all']),
});
export type ZeroCheckConfig = z.infer<typeof ZeroCheckConfigSchema>;

// Preset 2: Deviation Detection
export const DeviationConfigSchema = z.object({
  entity_type: z.string().min(1),
  metric: z.string().min(1),
  threshold_pct: z.number().min(0).max(100),
  reference: z.enum(['average', 'max']),
  min_value: z.number().optional(),
});
export type DeviationConfig = z.infer<typeof DeviationConfigSchema>;

// Custom Expression Builder
export const ExpressionVariableSchema = z.object({
  name: z.string().regex(/^v\d+$/),
  entity_id: z.string().min(1),
  metric: z.string().min(1),
});
export type ExpressionVariable = z.infer<typeof ExpressionVariableSchema>;

export const ExpressionConditionSchema = z.object({
  left: z.string().min(1),
  op: AlarmOperatorSchema,
  right: z.string().min(1),
});
export type ExpressionCondition = z.infer<typeof ExpressionConditionSchema>;

export const ExpressionConfigSchema = z.object({
  variables: z.array(ExpressionVariableSchema).min(1),
  conditions: z.array(ExpressionConditionSchema).min(1),
  combinator: z.enum(['AND', 'OR']),
});
export type ExpressionConfig = z.infer<typeof ExpressionConfigSchema>;

// Exclusion: skip specific entity+metric pairs during evaluation
export const RuleExclusionSchema = z.object({
  entity_id: z.string().min(1),
  metric: z.string().min(1),
});
export type RuleExclusion = z.infer<typeof RuleExclusionSchema>;

// Advanced Alarm Rule (wraps all 3 types)
export const AdvancedAlarmRuleSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(120),
  type: AdvancedRuleTypeSchema,
  enabled: z.boolean().default(true),
  severity: AlarmSeveritySchema,
  message: z.string().max(200).optional(),
  exclusions: z.array(RuleExclusionSchema).optional().default([]),
  zero_check: ZeroCheckConfigSchema.optional(),
  deviation: DeviationConfigSchema.optional(),
  expression: ExpressionConfigSchema.optional(),
});
export type AdvancedAlarmRule = z.infer<typeof AdvancedAlarmRuleSchema>;

// A single offending entity/metric that satisfied an advanced rule's condition.
// Display metadata only — never an alarm source.
export interface MatchedMetric {
  entity_id: string;
  metric: string;
  value: number | string | null;
}

// Triggered Advanced Rule (runtime, never persisted)
export interface TriggeredAdvancedRule {
  rule_id: string;
  rule_name: string;
  rule_type: AdvancedRuleType;
  severity: AlarmSeverity;
  message?: string;
  details: string;
  matched_metrics?: MatchedMetric[];
}

// ============================================
// Project Monitoring Config (top-level)
// ============================================

export const ProjectMonitoringConfigSchema = z.object({
  version: z.number().int().min(1).default(1),

  provider_state: z.object({
    connected: z.boolean().default(false),
    status: z.enum(['connected', 'disconnected', 'error']).default('disconnected'),
    provider_name: z.string().min(1).max(80).optional(),
    last_success_at: z.string().datetime().optional(),
    last_attempt_at: z.string().datetime().optional(),
    message: z.string().max(200).optional(),
  }),

  datapoints: z.array(DataPointConfigSchema).default([]),

  // Site-level cross-entity advanced rules
  advanced_rules: z.array(AdvancedAlarmRuleSchema).default([]),

  globals: z
    .object({
      stale_after_seconds: z.number().int().min(30).max(86400).default(300),
      enable_sound_alerts_default: z.boolean().default(true),
      fetchIntervalMs: z.number().int().min(60_000).max(3_600_000).optional(), // DO fetch interval
      active_hours: z.object({
        enabled: z.boolean().default(false),
        start: z.string().regex(/^\d{2}:\d{2}$/).default('05:00'),  // HH:MM local time
        end: z.string().regex(/^\d{2}:\d{2}$/).default('21:00'),
        timezone: z.string().default('Asia/Jerusalem'),  // IANA timezone
      }).optional(),
    })
    .optional(),
});
export type ProjectMonitoringConfig = z.infer<typeof ProjectMonitoringConfigSchema>;

// ============================================
// Provider / Credential Types
// ============================================

export type DataMode = 'upload' | 'live';

export interface CredentialStatus {
  hasCredentials: boolean;
  provider?: string;
  createdAt?: string;
  credentials?: Array<{
    provider_name: string;
    api_base_url: string;
    system_id?: string;
    auth_type?: string;
    is_active: number;
    last_test_at?: string;
    last_test_status?: string;
    last_test_message?: string;
    created_at?: string;
    updated_at?: string;
    consecutive_failures?: number;
    last_fetch_at?: string;
    last_fetch_status?: string;
    last_fetch_message?: string;
    circuit_open_until?: string;
    last_error_kind?: string;
  }>;
}

export type ProviderStatus = 'connected' | 'disconnected' | 'error' | 'testing';

// ============================================
// Self-Describing Provider Types
// ============================================

export interface CredentialField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'select';
  required: boolean;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
  mapTo: 'api_key' | 'api_secret' | 'system_id' | 'api_base_url' | 'extra_config';
  defaultValue?: string;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    message?: string;
  };
}

export interface ProviderRateLimits {
  callsPerMinute?: number;
  callsPerDay?: number;
  maxConcurrent?: number;
  description?: string;
}

export interface ProviderCapabilities {
  productionReady: boolean;
  supportsBulkFetch: boolean;
  supportsManualRefresh: boolean;
  supportsActiveHours: boolean;
  supportsWebhooks: boolean;
  httpMethod: 'GET' | 'POST';
  errorInBody: boolean;
  /** For bulk-fetch providers: fixed API calls per cycle, independent of data points. */
  bulkCallsPerCycle?: number;
}

export interface ProviderInfo {
  name: string;
  displayName: string;
  credentialFields: CredentialField[];
  defaultBaseUrl?: string;
  documentationUrl?: string;
  supportedAuthTypes: string[];
  icon?: string;
  rateLimits?: ProviderRateLimits;
  capabilities?: ProviderCapabilities;
}

// ============================================
// Provider Health (from DO)
// ============================================

export interface ProviderHealth {
  status: 'stopped' | 'running' | 'error' | 'circuit_open' | 'rate_limited' | 'disabled';
  message?: string;
  consecutiveFailures: number;
  circuitOpenUntil?: string;
  lastErrorKind?: string;
  lastFetchAt?: string;
  lastFetchStatus?: 'ok' | 'error';
  dailyCallCount?: number;
  dailyCallLimit?: number;
}

// ============================================
// Monitoring State Machine (derived, centralized)
// ============================================

export type MonitoringUIState =
  | 'NO_CREDENTIALS'
  | 'CREDENTIALS_SAVED'
  | 'CONNECTED'
  | 'SCHEMA_LOADED'
  | 'DATAPOINTS_CONFIGURED'
  | 'LIVE_ACTIVE'
  | 'CIRCUIT_OPEN'
  | 'ERROR';

// ============================================
// Schema Types (parsed entity tree)
// ============================================

export interface SchemaEntity {
  entity_id: string;
  entity_type: string;
  metrics: Array<{
    key: string;
    label?: string;
    unit?: string;
  }>;
}

export interface ParsedSchema {
  entities: SchemaEntity[];
  detectedProvider: string | null;
  rawCategories: string[];
  confidence: number;
  parsedAt: string;
}

// ============================================
// Active Alarm (from backend)
// ============================================

/**
 * Synthetic datapoint_id the SiteMonitor DO assigns to site-level advanced-rule
 * alarms (zero/threshold, deviation, expression) so they reuse the alarm_events
 * pipeline. Keep in sync with ADVANCED_ALARM_DATAPOINT_ID in
 * soleadlab-api/src/shared/alarmEval.ts.
 */
export const ADVANCED_ALARM_DATAPOINT_ID = '__advanced__';

export interface ActiveAlarmEvent {
  id: string;
  site_id: string;
  datapoint_id: string;
  rule_id?: string;
  rule_group_id?: string;
  metric: string;
  operator: string;
  threshold: number;
  actual_value: number | null;
  severity: AlarmSeverity;
  state: 'active' | 'acknowledged' | 'resolved' | 'dismissed';
  message?: string;
  triggered_at: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  resolved_at?: string;
  notes?: string;
}

// ============================================
// WebSocket Message Types (from DO)
// ============================================

export interface WSSnapshotMessage {
  type: 'snapshot';
  v: number;
  ts: number;
  entities: Array<{
    entity_id: string;
    entity_type: string;
    metrics: Record<string, number | string | null>;
  }>;
  activeAlarms: ActiveAlarmEvent[];
  health: ProviderHealth;
  schema: ParsedSchema | null;
  schemaHash: string;
}

export interface WSDataMessage {
  type: 'data';
  v: number;
  ts: number;
  entities: Array<{
    entity_id: string;
    entity_type: string;
    metrics: Record<string, number | string | null>;
  }>;
}

export interface WSAlarmMessage {
  type: 'alarm';
  v: number;
  ts: number;
  alarm: ActiveAlarmEvent;
}

export interface WSHealthMessage {
  type: 'health';
  v: number;
  ts: number;
  health: ProviderHealth;
}

export interface WSSchemaUpdatedMessage {
  type: 'schema_updated';
  v: number;
  ts: number;
  schemaHash: string;
  invalidDatapointIds: string[];
}

export interface WSErrorMessage {
  type: 'error';
  v: number;
  ts: number;
  kind: string;
  message: string;
}

export type WSMessage =
  | WSSnapshotMessage
  | WSDataMessage
  | WSAlarmMessage
  | WSHealthMessage
  | WSSchemaUpdatedMessage
  | WSErrorMessage;

// ============================================
// Validation Helpers
// ============================================

export function validateRulesAgainstMetrics(cfg: DataPointConfig): string[] {
  const errors: string[] = [];
  const selected = new Set(cfg.selected_metrics ?? []);

  cfg.rules?.forEach((r) => {
    if (!selected.has(r.metric)) {
      errors.push(
        `Rule ${r.id}: metric "${r.metric}" is not selected for datapoint "${cfg.display_name}".`
      );
    }
  });

  cfg.rule_groups?.forEach((g) => {
    g.conditions.forEach((c) => {
      if (!selected.has(c.metric)) {
        errors.push(
          `Rule group ${g.id}: metric "${c.metric}" is not selected for datapoint "${cfg.display_name}".`
        );
      }
    });
  });

  return errors;
}
